import * as Config from "config";
import FilterListIcon from "@mui/icons-material/FilterList";
import UpIcon from "@mui/icons-material/KeyboardArrowUp";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Fab from "@mui/material/Fab";
import Popover from "@mui/material/Popover";
import Zoom from "@mui/material/Zoom";
import { Theme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import { styled } from "@mui/material/styles";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import { ArrayChip, RangeChip, TextChip } from "marketplace/_views/filters/filter-chip";
import { FilterChips, FilterChipType } from "marketplace/_views/filters/filter-chips";
import Results from "marketplace/_views/results";
import SearchBar from "marketplace/_views/search-bar";
import SortButton from "marketplace/_views/sort-button";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { Filters } from "marketplace/search/filters";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams, useLoaderData } from "react-router";
import { useSession } from "users/__session/session-context";
import { DialogProps } from "_shared/_views/dialogs/dialog";
import FilterList from "marketplace/_views/filters/filter-list";
import ViewTeamProfile from "marketplace/team-profile/_views/view-team-profile";
import { useSelector } from "react-redux";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";

const PageContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  height: `calc(100vh - ${Config.elementHeight.appBar}px - ${Config.elementHeight.footer}px)`,
}));
const SearchContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  backgroundColor: "antiquewhite",
  borderRadius: theme.spacing(0.5),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3.5),
  paddingRight: theme.spacing(3.5),
  paddingTop: theme.spacing(2),
  position: "sticky",
  width: "100%",
  zIndex: 10,
}));
const MainContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
}));
const ResultsContainer = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  display: "grid",
  gridTemplateColumns: "1fr",
  [theme.breakpoints.down("xl")]: {
    marginTop: 0,
  },
}));
const ResultsPane = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    overflowX: "hidden",
    overflowY: "visible",
    padding: theme.spacing(0),
  },
  backgroundColor: "#FAFAFA",
  flex: 1,
  height: "100%",
  maxHeight: `calc(100vh - ${Config.elementHeight.appBar}px - ${Config.elementHeight.marketplaceSearchBar}px - ${Config.elementHeight.footer}px)`,
  overflowY: "auto",
  padding: theme.spacing(0, 1),
  position: "relative",
}));
const ChipContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    marginLeft: theme.spacing(1),
  },
}));
const SearchFilterChip = styled(Chip)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  marginRight: theme.spacing(1),
}));
const TopBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  backdropFilter: "blur(7px) saturate(200%)",
  backgroundColor: "rgba(250, 250, 250, 0.87)",
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(1, 0),
  position: "sticky",
  top: 0,
  zIndex: 10,
}));
const FilterButtons = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    marginLeft: theme.spacing(1),
  },
}));
const FilterListComponent = styled(FilterList)(() => ({
  position: "sticky",
  top: Config.elementHeight.appBar + Config.elementHeight.marketplaceSearchBar,
}));
const FilterContainer = styled("div")(({ theme }) => ({
  backgroundColor: "#FAFAFA",
  maxHeight: `calc(100vh - ${Config.elementHeight.appBar}px - ${Config.elementHeight.marketplaceSearchBar}px - ${Config.elementHeight.footer}px)`,
  width: theme.spacing(38),
  overflowX: "hidden",
  overflowY: "auto",
}));
const BackToTopButton = styled(Fab)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    bottom: theme.spacing(4),
  },
  [theme.breakpoints.down("xl")]: {
    right: theme.spacing(1),
  },
  position: "sticky",
  alignSelf: "center",
  justifySelf: "center",
  bottom: theme.spacing(1),
}));
const ShowResultsButton = styled(Button)(() => ({
  display: "block",
  margin: "auto",
}));

type MarketplaceProps = {
  embedded?: boolean;
  selectingLeader?: boolean;
  selectingMember?: boolean;
  onTeamMemberSelected?: (teamMember: Individual) => void;
};

export default function MarketplacePage(props: Readonly<MarketplaceProps>) {
  const { embedded, selectingLeader, selectingMember, onTeamMemberSelected } = props;

  const resultsRef = React.useRef<HTMLDivElement>(null);
  const filtersRef = React.useRef<FilterChips>(new FilterChips());

  const [searchTerm, setSearchTerm] = React.useState("");
  const [filters, setFilters] = React.useState<FilterChips>(filtersRef.current);
  const [sortBy, setSortBy] = React.useState("relevance");
  const [category, setCategory] = React.useState("all");
  const [numResults, setNumResults] = React.useState(0);
  const [filterPopoverAnchor, setFilterPopoverAnchor] = React.useState<HTMLButtonElement | null>(null);

  const { openDialog, closeDialog } = useDialog();

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const xlDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const xlUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeParams = useLoaderData();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const scrollTrigger = useScrollTrigger({
    target: resultsRef.current ?? undefined,
    disableHysteresis: true,
    threshold: 100,
  });

  // Helper function to parse query string and update filters
  function parseQueryString(
    searchParams: URLSearchParams,
    setSearchTerm: (term: string) => void,
    setCategory: (cat: string) => void,
    setSortBy: (sort: string) => void,
    handleFilterDisabled: (filter: FilterChipType) => void,
    handleFilterChanged: (filter: FilterChipType) => void,
    setFilters: (chips: FilterChips) => void,
  ) {
    const filters: FilterChipType[] = Filters;

    if (searchParams.has("query")) setSearchTerm(searchParams.get("query") ?? "");
    if (searchParams.has("category")) setCategory(searchParams.get("category") ?? "all");
    if (searchParams.has("sortBy")) setSortBy(searchParams.get("sortBy") ?? "relevance");

    for (const [filterName, filterValue] of searchParams.entries()) {
      let parsedFilter = filters.find((filter) => filter.filterName === filterName);

      if (!parsedFilter) continue;

      if (filterName === "query" && filterValue === "") {
        handleFilterDisabled(parsedFilter);
        continue;
      }

      if (filterValue === "") {
        parsedFilter.value = [];
      } else {
        parsedFilter.value = filterValue.split(",");
      }

      parsedFilter.enable();

      handleFilterChanged(parsedFilter);
    }

    setFilters(new FilterChips(filters));
  }

  // Helper function to load route profile and open dialog
  function loadRouteProfile(
    type: string | undefined,
    id: string | undefined,
    openDialog: (props: DialogProps, onClose?: () => void) => void,
    navigate: (path: string) => void,
  ) {
    if (!type || !id) return;

    const guidId = new Guid(id);
    const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

    if (type === "individual") {
      openDialog(
        {
          component: <ViewIndividualProfile individualId={guidId} />,
          MuiProps: {
            fullWidth: true,
            maxWidth: "lg",
          },
          contentSxProps: {
            display: "flex",
          },
          titleStyle: {
            paddingBottom: 0,
          },
        },
        () => navigate(parentPath.pathname),
      );
    } else if (type === "team") {
      openDialog(
        {
          component: <ViewTeamProfile teamId={guidId} />,
          MuiProps: {
            fullWidth: true,
            maxWidth: "lg",
          },
          contentSxProps: {
            display: "flex",
          },
          titleStyle: {
            paddingBottom: 0,
          },
        },
        () => navigate(parentPath.pathname),
      );
    } else if (type === "company") {
      openDialog(
        {
          component: <ViewCompanyProfile companyId={guidId} />,
          MuiProps: {
            fullWidth: true,
            maxWidth: "lg",
          },
          contentSxProps: {
            display: "flex",
          },
          titleStyle: {
            paddingBottom: 0,
          },
        },
        () => navigate(parentPath.pathname),
      );
    }
  }

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    filtersRef.current.addRange(Filters);
    setFilters(new FilterChips(filtersRef.current.chips));
    parseQueryString(
      searchParams,
      setSearchTerm,
      setCategory,
      setSortBy,
      handleFilterDisabled,
      handleFilterChanged,
      setFilters,
    );
  }, [searchParams]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    if (!routeParams?.type || !routeParams?.id) return;
    const type = routeParams.type;
    const id = routeParams.id;

    loadRouteProfile(type, id, openDialog, navigate);
  }, [routeParams]);

  function handleFilterChanged(updatedFilter: FilterChipType) {
    if (!updatedFilter.value) return;

    const targetChip = filtersRef.current.findByName(updatedFilter.filterName);

    if (!targetChip) return;

    if (targetChip instanceof ArrayChip || targetChip instanceof RangeChip) {
      filtersRef.current.updateValue(updatedFilter.filterName, updatedFilter.value as string[]);
    } else {
      filtersRef.current.updateValue(updatedFilter.filterName, updatedFilter.value as string);
    }

    setFilters(new FilterChips(filtersRef.current.chips));

    let params = searchParams;
    params.set(updatedFilter.filterName, updatedFilter.value.toString());
    setSearchParams(params);
  }

  function handleFilterEnabled(updatedFilter: FilterChipType) {
    filtersRef.current.enable(updatedFilter.filterName);
    setFilters(new FilterChips(filtersRef.current.chips));
  }

  function handleFilterDisabled(updatedFilter: FilterChipType) {
    filtersRef.current.disable(updatedFilter.filterName);
    setFilters(new FilterChips(filtersRef.current.chips));

    let params = searchParams;
    params.delete(updatedFilter.filterName);
    setSearchParams(params);

    if (updatedFilter.filterName === "query") {
      setSearchTerm("");
    }
  }

  function handleCategoryChanged(category: Config.Category) {
    setNumResults(0);
    setSearchTerm("");
    setCategory(category.id);
    setSortBy("relevance");

    filtersRef.current.disableAll();
    filtersRef.current.addRange(Filters);

    if (category.id !== "all") {
      filtersRef.current.enable(category.id);
      const updatedFilter = new TextChip("category", category.name);
      handleFilterChanged(updatedFilter);
    }

    setFilters(new FilterChips(filtersRef.current.chips));

    let params = searchParams;
    params.set("category", category.id);
    params.set("pageIndex", "0");
    setSearchParams(params);
  }

  function handleSearch(term: string = "") {
    setSearchTerm(term);
    filtersRef.current.updateValue("query", term);
    filtersRef.current.enable("query");
    setFilters(new FilterChips(filtersRef.current.chips));

    let params = searchParams;
    params.set("query", term);
    setSearchParams(params);

    navigate({ search: params.toString() });
  }

  function handleSort(sortField: string) {
    setSortBy(sortField);
    let params = searchParams;
    params.set("sortBy", sortField);
    setSearchParams(params);
  }

  function openFilterDialog() {
    const dialogProps: DialogProps = {
      MuiProps: {
        fullScreen: true,
        keepMounted: true,
        disablePortal: true,
      },
      title: (
        <ShowResultsButton
          size="large"
          variant="contained"
          disableElevation={true}
          color="primary"
          onClick={() => closeDialog()}
        >
          Show {numResults} Result{numResults === 1 ? null : "s"}
        </ShowResultsButton>
      ),
      component: (
        <FilterList
          dialog={true}
          filters={filters}
          onChange={handleFilterChanged}
          onAdded={handleFilterEnabled}
          onRemoved={handleFilterDisabled}
        />
      ),
    };
    openDialog(dialogProps);
  }

  if (!sessionPermissions?.includesAccessToMarketplace) {
    return <NotAuthorized />;
  }

  return (
    <PageContainer>
      <SearchContainer>
        <SearchBar
          searchTerm={searchTerm}
          onCategoryChanged={handleCategoryChanged}
          onSearch={handleSearch}
          onSearchCleared={() => {
            handleFilterDisabled(filtersRef.current.findByName("query")!);
            handleSearch();
          }}
        />
      </SearchContainer>
      <MainContent>
        {xlUp && (
          <FilterContainer>
            <FilterListComponent
              filters={filters}
              dialog={false}
              onChange={handleFilterChanged}
              onAdded={handleFilterEnabled}
              onRemoved={handleFilterDisabled}
            />
          </FilterContainer>
        )}
        <ResultsPane ref={resultsRef}>
          <TopBar>
            <ChipContainer>
              {filters.chips.every((chip: FilterChipType) => !chip.enabled) && (
                <SearchFilterChip label="No Active Filters" disabled size={mdDown ? "small" : "medium"} />
              )}
              {filters.chips.map((chip: FilterChipType) => (
                <SearchFilterChip
                  key={chip.filterName}
                  label={chip.label}
                  size={mdDown ? "small" : "medium"}
                  style={{ display: chip.enabled ? "inline-flex" : "none" }}
                  onDelete={() => handleFilterDisabled(chip)}
                />
              ))}
            </ChipContainer>
            {/*xlUp && <SortDropdown sortBy={sortBy} onSort={handleSort} />*/}
            {xlDown && (
              <FilterButtons>
                <Button
                  size="large"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  onClick={(event) => (mdUp ? setFilterPopoverAnchor(event.currentTarget) : openFilterDialog())}
                >
                  Filters
                </Button>
                <Popover
                  anchorEl={filterPopoverAnchor}
                  open={Boolean(filterPopoverAnchor)}
                  onClose={() => setFilterPopoverAnchor(null)}
                >
                  <FilterList
                    dialog={true}
                    filters={filters}
                    onChange={handleFilterChanged}
                    onAdded={handleFilterEnabled}
                    onRemoved={handleFilterDisabled}
                  />
                </Popover>
                <SortButton sortBy={sortBy} onSort={handleSort} />
              </FilterButtons>
            )}
          </TopBar>
          <ResultsContainer>
            <Results
              session={session}
              embedded={embedded}
              selectingTeamLeader={selectingLeader}
              selectingTeamMember={selectingMember}
              category={category}
              searchTerm={searchTerm}
              filters={filters.toDictionary()}
              sortBy={sortBy}
              navigate={navigate}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              onResults={(results: number | undefined) => setNumResults(results ?? 0)}
              onTeamMemberSelected={onTeamMemberSelected}
            />
            <Zoom in={scrollTrigger}>
              <BackToTopButton
                color="primary"
                size="medium"
                variant="extended"
                onClick={() => {
                  if (resultsRef.current) resultsRef.current.scrollTop = 0;
                }}
              >
                <UpIcon />
                Back To Top
              </BackToTopButton>
            </Zoom>
          </ResultsContainer>
        </ResultsPane>
      </MainContent>
    </PageContainer>
  );
}
